import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import expressionStartingPointImage from '../../../assets/images/expressionStartingPoint.png';
import {ExpressionsPicker} from './expressionsPicker';
import {ExpressionDependencies, ExpressionStateActions} from './expressionState';

/*
 * Props.
 */

interface ExpressionsSidePanelCreateProps {
  onAddExpression: ExpressionStateActions['onAddExpression'];
  expressionsDependencies: ExpressionDependencies;

  helpPageUrl?: string;
}

/*
 * Style.
 */

const StyledExpressionsSidePanelCreateWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h2 {
    margin-top: 24px;
    margin-bottom: 8px;
    font-size: ${fontSizes.veryLarge};
    font-weight: ${fontWeights.semibold};
    color: ${(p) => p.theme.greys.shade80};
  }

  p {
    margin-top: 0px;
    color: ${(p) => p.theme.greys.shade70};
    width: 310px;
  }

  img {
    width: 259px;
    margin-top: 24px;
    margin-bottom: 24px;
  }
`;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const StyledLearnMoreButtonContent = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: min-content min-content;
  grid-gap: 4px;
`;

/*
 * Component.
 */

// @TODO: Change explanation (should not refer to "smart rules").

export const ExpressionsSidePanelCreate: FC<ExpressionsSidePanelCreateProps> = (props) => {
  const {onAddExpression, helpPageUrl, expressionsDependencies} = props;

  return (
    <StyledExpressionsSidePanelCreateWrapperDiv>
      <h2>
        <Trans id="editor-expression-side-panel-create-title">Starting creating a dynamic variable</Trans>
      </h2>
      <p>
        <Trans id="editor-expression-side-panel-create-explanation">
          Build the step-by-step path to the information that you need to start creating smart rules.
        </Trans>
      </p>
      {helpPageUrl && (
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => openInBrowser(helpPageUrl)}>
          <StyledLearnMoreButtonContent>
            <Trans id="editor-expression-button-learn-more">Learn more</Trans>
            <Icon name="externalLinkSmall" />
          </StyledLearnMoreButtonContent>
        </Button>
      )}
      <img src={expressionStartingPointImage} alt="" />
      <ExpressionsPicker onAddExpression={onAddExpression} dependencies={expressionsDependencies}>
        <StyledButton variant={ButtonVariantsEnum.PRIMARY}>
          <Trans id="editor-expression-side-panel-starting-point-title">Start dynamic variable path</Trans>
        </StyledButton>
      </ExpressionsPicker>
    </StyledExpressionsSidePanelCreateWrapperDiv>
  );
};
